/**
 * fx.js — Foreign-exchange helper for multi-currency cost inputs.
 *
 * Rates are static reference rates against USD (base = 1). They are intended
 * for **preliminary economic studies** only — production DCF models should
 * pull spot / forward rates from a data provider.
 *
 * Currency list matches /app/frontend/src/lib/currencies.js so the picker UI
 * stays consistent everywhere.
 */

// Static reference rates: 1 USD = X {currency}
// Refreshed 2026-02. Update as needed.
export const FX_USD = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  INR: 83.20,
  AUD: 1.52,
  CAD: 1.36,
  ZAR: 18.50,
  BRL: 5.05,
  CLP: 950.0,
  CNY: 7.20,
  JPY: 151.0,
  CHF: 0.88,
};

/**
 * Convert a numeric amount from one currency to another.
 * Rounds to 4 decimal places to avoid FP noise.
 *
 * @param {number} amount   Value in `from` currency
 * @param {string} from     Source currency ISO code
 * @param {string} to       Target currency ISO code
 * @returns {number}        Equivalent value in `to` currency
 */
export function convert(amount, from, to) {
  const n = Number(amount) || 0;
  if (!from || !to || from === to) return n;
  const fromRate = FX_USD[from] ?? 1;
  const toRate = FX_USD[to] ?? 1;
  // amount → USD → target
  const usd = n / fromRate;
  return Math.round(usd * toRate * 10000) / 10000;
}

/**
 * Format an FX conversion preview: "≈ ₹41,600 / t"
 */
export function fxPreview(amount, from, to, symbol) {
  if (!from || !to || from === to) return "";
  const v = convert(amount, from, to);
  if (!Number.isFinite(v)) return "";
  const formatted = v.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return `≈ ${symbol}${formatted}`;
}

export const FX_SUPPORTED = Object.keys(FX_USD);
